// Firebase Cloud Messaging (FCM) — request permission, get token, save to backend.
import { initializeApp } from "firebase/app";
import { getMessaging, getToken, isSupported, deleteToken } from "firebase/messaging";
import api from "@/lib/api";
import { FIREBASE_CONFIG, FIREBASE_VAPID_KEY } from "@/lib/firebase-config";

let _app = null;
let _messaging = null;
let _lastToken = null;

async function _init() {
  if (!(await isSupported())) throw new Error("unsupported");
  if (!_app) _app = initializeApp(FIREBASE_CONFIG);
  if (!_messaging) _messaging = getMessaging(_app);
  return _messaging;
}

export async function fcmSupported() {
  try { return await isSupported(); } catch { return false; }
}

export async function enableFcm() {
  const perm = await Notification.requestPermission();
  if (perm !== "granted") throw new Error("denied");
  const m = await _init();
  // Force-refresh: delete any cached token first so we always get a FRESH one
  // bound to the current VAPID + project. Fixes iOS caching a stale token.
  try { await deleteToken(m); } catch { /* no cached token — that's fine */ }
  const reg = await navigator.serviceWorker.getRegistration("/firebase-cloud-messaging-push-scope")
    || await navigator.serviceWorker.register("/firebase-messaging-sw.js");
  const token = await getToken(m, { vapidKey: FIREBASE_VAPID_KEY, serviceWorkerRegistration: reg });
  if (!token) throw new Error("no-token");
  _lastToken = token;
  await api.post("/push/register-fcm-token", { token });
  return token;
}

export async function disableFcm() {
  try { const m = await _init(); await deleteToken(m); } catch { /* ignore */ }
  const body = _lastToken ? { token: _lastToken } : {};
  await api.post("/push/unregister-fcm-token", body);
  _lastToken = null;
}

export async function currentFcmToken() { return _lastToken; }

export async function testFcmPush() {
  const { data } = await api.post("/push/test", {});
  return data || { sent: 0 };
}

export async function triggerFcmDueCheck() {
  try { const { data } = await api.post("/push/run-check", {}); return data?.sent || 0; }
  catch { return 0; }
}
